import React from 'react';

import { useSelector } from 'react-redux';

import { arcConvertUnit, arcSpatiumUnit } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { useUnitType } from '@src/core/features/settings';

import { terrainCacheKey } from '@src/features/order/pages/upsert-mission-page/config/cache-keys';
import { CRASH_ZONE_BUFFER } from '@src/features/order/pages/upsert-mission-page/config/terrain';
import {
  selectTerrainMode,
  selectWaypoints,
  useGetTerrainAwareMutation,
} from '@src/features/order/pages/upsert-mission-page/redux';
import { TerrainMode } from '@src/features/order/pages/upsert-mission-page/redux/extra-store/types';
import {
  analyzeIntervals,
  calculateDistance,
  interpolateAltitude,
} from '@src/features/order/pages/upsert-mission-page/utils';

// NOTE: This hook is the single source of truth for terrain-aware series and
// collision checks shared between the chart (TerrainAware) and the alert
// (TerrainAlert). It only READS the RTK Query cache via `fixedCacheKey`; the
// mutation trigger lives in TerrainAware to avoid duplicate POSTs.
const useTerrainSeries = () => {
  const wps = useSelector(selectWaypoints);
  const terrainMode = useSelector(selectTerrainMode);
  const isAmsl = terrainMode === TerrainMode.AMSL;
  const isAgl = terrainMode === TerrainMode.AGL;

  const [, { data = [], isLoading }] = useGetTerrainAwareMutation({
    fixedCacheKey: terrainCacheKey,
  });

  const isInitialLoading = isLoading && data.length === 0;

  const unitType = useUnitType();
  const buffer = arcConvertUnit(CRASH_ZONE_BUFFER, MeasurementType.DISTANCE, unitType);
  const unit = arcSpatiumUnit(unitType);


  const hasHomeStart = data.length > 0 && data[0]?.label === 'H';
  const hasHomeEnd = data.length > 1 && data[data.length - 1]?.label === 'H';

  const terrainPoints = React.useMemo(() => {
    if (!hasHomeStart || hasHomeEnd) return data;
    return [...data, data[0]];
  }, [data, hasHomeStart, hasHomeEnd]);

  const chartWps = React.useMemo(() => {
    if (wps.length === 0) return wps;

    const takeoffAlt = Number(wps[0]?.altitude ?? 0);
    const returnAlt = Number(wps[wps.length - 1]?.altitude ?? takeoffAlt);
    const homePoint = hasHomeStart ? data[0] : null;

    const start = homePoint
      ? [{
        latitude: homePoint.latitude,
        longitude: homePoint.longitude,
        altitude: takeoffAlt,
        label: 'H',
      }]
      : [];

    const end = homePoint
      ? [{
        latitude: homePoint.latitude,
        longitude: homePoint.longitude,
        altitude: returnAlt,
        label: 'H',
      }]
      : [];

    return [...start, ...wps, ...end];
  }, [wps, data, hasHomeStart]);

  const wpDistances = React.useMemo(() => calculateDistance(chartWps), [chartWps]);

  const minTerrainAlt = React.useMemo(() => {
    if (terrainPoints.length === 0) return 0;
    return Math.min(...terrainPoints.map((d) => Number(d.altitude)));
  }, [terrainPoints]);

  // RELATIVE ("Relative to Launch") uses takeoff terrain AMSL as the chart's zero,
  const launchTerrainAlt = React.useMemo(() => {
    if (data.length === 0) return 0;
    const launchIndex = hasHomeStart ? 1 : 0;
    return Number(data[launchIndex]?.altitude ?? data[0]?.altitude ?? 0);
  }, [data, hasHomeStart]);

  const convertAltitude = React.useCallback(
    (value: number) =>
      arcConvertUnit(
        value,
        MeasurementType.DISTANCE,
        unitType
      ),
    [unitType]
  );

  const droneData = React.useMemo(() => {
    if (data.length === 0) {
      return wpDistances.map((wp) => ({
        ...wp,
        // wp.altitude is already in the user's display unit — do NOT re-convert.
        altitude: Number(wp.altitude),
      }));
    }

    // AMSL + Relative to Launch
    if (!isAgl) {
      return wpDistances.map((wp) => ({
        ...wp,
        // wp.altitude is already in the user's display unit — do NOT re-convert.
        altitude: Number(wp.altitude),
      }));
    }

    // AGL
    if (wpDistances.length < 2) {
      return wpDistances.map((wp) => ({
        ...wp,
        // wp.altitude is already in the user's display unit — do NOT re-convert.
        altitude: Number(wp.altitude),
      }));
    }

    const terrainWithDist = calculateDistance(terrainPoints);

    return wpDistances.map((wp) => {
      const terrainAtWp = interpolateAltitude(
        wp.distance,
        terrainWithDist
      );

      const relTerrain = terrainAtWp - minTerrainAlt;

      return {
        // relTerrain is SI (convert to display); wp.altitude is already display.
        ...wp,
        altitude: convertAltitude(relTerrain) + Number(wp.altitude),
      };
    });
  }, [
    wpDistances,
    isAgl,
    data,
    terrainPoints,
    minTerrainAlt,
    convertAltitude,
  ]);

  const terrainData = React.useMemo(() => {
    let terrain;

    if (isAmsl) {
      terrain = calculateDistance(terrainPoints);
    } else if (isAgl) {
      terrain = calculateDistance(terrainPoints, minTerrainAlt);
    } else {
      terrain = calculateDistance(terrainPoints, launchTerrainAlt);
    }

    return terrain.map((p) => ({
      ...p,
      altitude: convertAltitude(Number(p.altitude)),
    }));
  }, [
    terrainPoints,
    isAmsl,
    isAgl,
    minTerrainAlt,
    launchTerrainAlt,
    convertAltitude,
  ]);

  const crashZoneData = React.useMemo(
    () => terrainData.map((p) => ({ ...p, altitude: Number(p.altitude) + buffer })),
    [terrainData, buffer],
  );

  const hasTerrainCollision = React.useMemo(
    () => analyzeIntervals(droneData, terrainData, 0).length > 0,
    [droneData, terrainData],
  );

  const hasCrashZoneCollision = React.useMemo(
    () => analyzeIntervals(droneData, terrainData, buffer).length > 0,
    [droneData, terrainData, buffer],
  );

  return {
    isLoading,
    isInitialLoading,
    isAmsl,
    isAgl,
    droneData,
    terrainData,
    crashZoneData,
    wpDistances,
    buffer,
    unit,
    hasTerrainCollision,
    hasCrashZoneCollision,
  };
};

export default useTerrainSeries;
